/**
 * Embed mode related helpers
 * @author Nachiket Kakatkar <nachiket@helpshift.com>
 * @created Feb 15, 2023
 * @module helpers/embedMode
 */

import embedModeEvents from "constants/embedModeEvents";
import lsUtils from "gunpowder/utils/localStorage";
import routingHelpers from "helpers/routing";

const EMBED_MODE_BASE_PATH = "/e/admin/";
const EMBED_MODE_PARENT_LS_KEY = "EMBED_MODE_PARENT";
const COMPACT_MODE_URL = "issue-compact";
const DEFAULT_MODE_URL = "issue";

/**
 * Returns whether embed mode is enabled for the domain
 * @returns {boolean}
 */
const isEmbedModeEnabled = () => {
  return HS.features.embed_mode_enabled && location.pathname.startsWith(EMBED_MODE_BASE_PATH);
};

/**
 * Returns whether the current route is the compact view of the embedded dashboard
 * @returns {boolean}
 */
const isEmbedModeCompactView = () => {
  return isEmbedModeEnabled() && location.pathname.includes(COMPACT_MODE_URL);
};

/**
 * Returns issue details path for the given issue publish id depending on the mode of the view.
 * @TODO: feature/compact-embedded-dash - Move this to a common routing util instead of having it
 * as part of embed mode helpers.
 * @param {string} publishId - Publish id of the issue
 * @returns {string}
 */
const getIssueDetailsPath = (publishId) => {
  const basePath = routingHelpers.getAdminBasePath();
  let path = `${DEFAULT_MODE_URL}/`;

  if (isEmbedModeCompactView()) {
    path = `${COMPACT_MODE_URL}/`;
  }

  return `${basePath}${path}${publishId}/`;
};

/**
 * Returns the embed mode base path for dashboard
 * @returns {string}
 */
const getEmbedModeDashboardBasePath = () => EMBED_MODE_BASE_PATH;

/**
 * Returns the dashboard event name for the given post message
 * event. If the post message event name is not white listed for
 * dashboard handling, it will return empty string
 * @param {string} postMsgEventName - The event name received in the postMessage handler
 * @returns {string} - Dashboard event name corresponding to this event
 */
const getApiDashboardEventName = (postMsgEventName) => {
  const dashboardEventName = embedModeEvents[postMsgEventName];

  return dashboardEventName ? `embed:${dashboardEventName}` : "";
};

/**
 * Returns the value of the parent origin saved in localStorage. This value
 * gets saved when the embed mode is initialized and the page is loaded with
 * the parentOrigin value set in the HS object
 *
 * @returns {string}
 */
const getParentOrigin = () => {
  return lsUtils.getItem(EMBED_MODE_PARENT_LS_KEY);
};

/**
 * Saves the given parent origin value in local storage
 * @param {string} parentOrigin - Value of parent origin
 */
const saveParentOrigin = (parentOrigin) => {
  lsUtils.setItem(EMBED_MODE_PARENT_LS_KEY, parentOrigin);
};

export default {
  isEmbedModeEnabled,
  getEmbedModeDashboardBasePath,
  getApiEventName: getApiDashboardEventName,
  getParentOrigin,
  saveParentOrigin,
  isEmbedModeCompactView,
  getIssueDetailsPath
};
